import { Config } from '../config/config';
import { MAX_WMTS_FRAMES, MAX_DEEP_CHECKS } from '../config/constants';
import { calcPrefixFromDate, cloneDate, dateToUTCString, getLatestDates, getDateFromUTCDateStr, subtractDaysFromDate, localDateToNoonUTC } from '../helpers/tools';
import { refsStore, datasetStore, layersStore } from '../managers/globalState';

// Threshold is 5%
const MAX_BLACKNESS = 5;
const CACHED_BLACKNESS = {};
const AVAILABLE_IMAGES = {};

// DS: finding the most optimal image size.
// 2048 x 1024: 39% black - 100% accuracy - takes 67s
// 1024 x 512: 37% black - 95% accuracy - takes 19s
// 512 x 256: 35% black - 89% accuracy - takes 4s
// 256 x 128: 34% black - 85% accuracy - takes 2.5s - most optimal
// 128 x 64: 23% black - 58% accuracy - takes 2.2s
// 64 x 32: 22% black - 57% accuracy - takes 2s

// Unknown: how optimal is this server to return images after the request.
// If we really want to get the fastest result, we can save every single image on our end but it might be too much.
const WIDTH = 256;
const HEIGHT = 128;
const cubeFallbackType = 'cube';
const FALLBACKS = [
	// SNPP version
	{
		type: 'wmts',
		title: 'Suomi NPP / VIIRS Daily Mosaics',
		endpoint: 'assets/wmts_xml_snpp',
		layer: 'VIIRS_SNPP_CorrectedReflectance_TrueColor',
		template: `https://wvs.earthdata.nasa.gov/api/v1/snapshot?REQUEST=GetSnapshot&LAYERS=VIIRS_SNPP_CorrectedReflectance_TrueColor&CRS=EPSG:4326&TIME={YYYY-MM-DD}&WRAP=DAY&BBOX=-90,-180,90,180&FORMAT=image/jpeg&WIDTH=${WIDTH}&HEIGHT=${HEIGHT}&AUTOSCALE=TRUE`
	},
	// NOAA20 version
	{
		type: 'wmts',
		title: 'NOAA-20 / VIIRS Daily Mosaics',
		endpoint: 'assets/wmts_xml_noaa',
		layer: 'VIIRS_NOAA20_CorrectedReflectance_TrueColor',
		template: `https://wvs.earthdata.nasa.gov/api/v1/snapshot?REQUEST=GetSnapshot&LAYERS=VIIRS_NOAA20_CorrectedReflectance_TrueColor&CRS=EPSG:4326&TIME={YYYY-MM-DD}&WRAP=DAY&BBOX=-90,-180,90,180&FORMAT=image/jpeg&WIDTH=${WIDTH}&HEIGHT=${HEIGHT}&AUTOSCALE=TRUE`
	},
	// cubemap fallback
	{
		type: cubeFallbackType,
		title: 'Suomi NPP / VIIRS Daily Mosaics'
	}
];

class VisibleEarthManager {
	/**
	 * Constructor
	 * @param {object} pioneer Pioneer engine
	 * @param {object} scene Pioneer main scene
	 * @private
	 */
	constructor(app) {
		this.app = app;

		this._tileMatrixSet = '250m';

		this.numOfFrames = 7;
		this.currentIndex = 0;

		this.currentData = null;

		this.earth = this.app.scene.get('earth');

		this.earth.getComponentByType('atmosphere')?.setEnabled(false);
		this.earth.getComponentByType('spheroidLOD')?.setEnabled(false);

		this.updateWMTSMaxLevel = this.updateWMTSMaxLevel.bind(this);

		datasetStore.subscribeAll(this.updateWMTSMaxLevel);
	}

	/**
	 * Calculate blackness of the image
	 * @param {string} url - url of the base image of the wmts
	 * @returns {number}
	 */
	calculateBlackness(url, start = 0, end = 0, MAX_BLACKNESS = 100) {
		return new Promise(resolve => {
			if (CACHED_BLACKNESS[url]) {
				return resolve(CACHED_BLACKNESS[url]);
			}

			const img = new Image();
			img.src = url;
			img.crossOrigin = 'Anonymous';

			try {
				img.addEventListener('load', () => {
					const canvas = document.createElement('canvas');
					const ctx = canvas.getContext('2d');

					canvas.width = img.width;
					canvas.height = img.height;

					ctx.drawImage(img, 0, 0);

					const rgbaData = ctx.getImageData(0, 0, img.width, img.height);

					let numOfBlackPixel = 0;
					const xs = start || 0;
					const ys = end || img.height;

					let blackness = 0;
					// This FOR loop goes through single dimension image data array with rgba values which is why we jump 4 elements (r, g, b and a) in the loop
					for (let i = xs * img.width * 4; i < img.width * ys * 4; i += 4) {
						if (rgbaData.data[i + 0] === 0 && rgbaData.data[i + 1] === 0 && rgbaData.data[i + 2] === 0) {
							numOfBlackPixel += 1;
						} else if (rgbaData.data[i + 0] === undefined || rgbaData.data[i + 1] === undefined || rgbaData.data[i + 2] === undefined) {
							numOfBlackPixel += 1;
						}

						blackness = numOfBlackPixel * 100 / ((ys - xs) * img.width);
						if (blackness > MAX_BLACKNESS) {
							CACHED_BLACKNESS[url] = blackness;
							return resolve(Math.ceil(blackness));
						}
					}

					blackness = numOfBlackPixel * 100 / ((ys - xs) * img.width);

					CACHED_BLACKNESS[url] = blackness;
					resolve(Math.ceil(blackness));
				});

				// if there is an issue with an image or it does not exist yet
				img.addEventListener('error', () => {
					const blackness = 100;
					CACHED_BLACKNESS[url] = blackness;
					resolve(blackness);
				});
			} catch {
				// If image fails to load, return 100% black
				console.warn('Warning in calculateBlackness');
				const blackness = 100;
				CACHED_BLACKNESS[url] = blackness;
				resolve(blackness);
			}
		});
	}

	/**
	 * Check if image exists
	 * @param {string} url - url of the image
	 * @returns {Promise<boolean>}
	 */
	isImage(url) {
		return new Promise(resolve => {
			if (AVAILABLE_IMAGES[url] === true || AVAILABLE_IMAGES[url] === false) {
				return resolve(AVAILABLE_IMAGES[url]);
			}

			const img = new Image();
			img.src = url;
			img.crossOrigin = 'Anonymous';

			try {
				img.addEventListener('load', () => {
					AVAILABLE_IMAGES[url] = true;
					resolve(AVAILABLE_IMAGES[url]);
				});

				// if there is an issue with an image or it does not exist yet
				img.addEventListener('error', () => {
					AVAILABLE_IMAGES[url] = false;
					resolve(AVAILABLE_IMAGES[url]);
				});
			} catch {
				// If image fails to load, return 100% black
				console.warn('Warning in calculateBlackness');
				AVAILABLE_IMAGES[url] = false;
				resolve(AVAILABLE_IMAGES[url]);
			}
		});
	}

	setupWMTS(key, data) {
		const { date, layer, endpoint, minLevel } = data;

		// insufficient data
		if (!date || !layer || !endpoint || !key) {
			return null;
		}

		const wmts = this.earth.getComponent(key) || this.earth.addComponent('wmts', key);

		if (!wmts) {
			return null;
		}

		// Set the end point, layer, and tile matrix set.
		wmts.setEndPoint(endpoint);
		wmts.setLayer(layer);
		wmts.setTileMatrixSet(this._tileMatrixSet);
		wmts.setDimensionValue('Time', date);

		wmts.setMinLevel(minLevel);

		wmts.setVisible(false);

		return wmts;
	}

	setupCubemap(lodName, cubeTexturePath) {
		const existingComponent = this.earth.getComponent(lodName);

		if (existingComponent) {
			return existingComponent;
		}

		const lod = this.earth.addComponent('spheroidLOD', lodName);
		lod.setEnabled(true);
		lod.setVisible(false);

		lod.setCubeMapLayout([
			['+z', '+x', '+y'],
			['+z', '+x', '-y'],
			['-y', '+x', '-z'],
			['-y', '+x', '+z'],
			['-y', '+z', '-x'],
			['-y', '+z', '+x']
		]);
		lod.setMapping('cube');
		lod.setFeature('decalMap', false);
		lod.setTexture('color', cubeTexturePath);

		return lod;
	}

	preload() {
		if (!this.currentData) {
			return;
		}

		for (let i = 0; i < this.currentData?.length; i += 1) {
			const { type, url: cubeTexturePath } = this.currentData[i];

			if (type === 'wmts') {
				this.setupWMTS(`viirs${i}`, this.currentData[i]);
			} else {
				this.setupCubemap(`cube${i}`, cubeTexturePath);
			}
		}
	}

	/**
	 * Checks the SNPP and NOAA WMTS servers  by downloading a small image.
	 */
	async checkWTMSServer() {
		// These are the default images to check if server is down
		const defaultSNPP = 'https://gitc.earthdata.nasa.gov/wmts/epsg4326/best/VIIRS_SNPP_CorrectedReflectance_TrueColor/default/';
		const defaultNOAA = 'https://gitc.earthdata.nasa.gov/wmts/epsg4326/best/VIIRS_NOAA20_CorrectedReflectance_TrueColor/default/';

		const isUp = await this.isImage(defaultSNPP) || await this.isImage(defaultNOAA);
		return isUp;
	}

	/**
	 * Get array of available days with dates, types, layers and endpoints
	 */
	async setCurrentData() {
		const { get } = refsStore.stateSnapshot;

		/**
		 * The viirs cubemap animations array is calculated in the dataset manager.
		 * It has taken into account start, end and missingDates from the viirs cubemap manifest file.
		 * It is not used for the 1st and 2nd WMTS fallbacks (if date range is 7 days or less).
		 *
		 * If the date range is more than MAX_WMTS_FRAMES (7), we automatically use the viirs cubemaps.
		 *
		 * If less than than the MAX_WMTS_FRAMES (7),
		 * we will deep check the 1st and 2nd WMTS fallbacks (which wont have manifest data restrictions applied)
		 * If there is any missing dates for the WMTS, the viirs cubemaps should fill in the gaps.
		 */
		const {
			animations,
			animationEndDate,
			hasAnimation,
			setIsLatest,
			setIsLatestSeven,
			calcNumFrames,
			getDatasetManifestData,
			setAnimationProps
		} = get('dataset');

		/**
		 * There's not a consistently reliable way to check whether the endDate is the latest.
		 * This is fundamentally due to not having a NOAA-20 manifest data file.
		 * For example:
		 * 1) The last Suomi data was 3 days ago
		 * 2) A user enters the end date in the query of 2 days ago.
		 * 3) With this input, we dont do a deep check for 1 day ago,
		 * so we dont know if query input of 2 days ago is the 'latest'
		 *
		 * The only way we know for the 'latest' for certain is if our end date is today.
		 */
		let isLatest = animationEndDate === null;

		// Get number of frames.
		this.numOfFrames = calcNumFrames();

		// Declare framesData var.
		const framesData = [];

		// Determine if we need to update the dataset animation props.
		let updateAnimationProps = true;

		// Check fallbacks if date range <= MAX_WMTS_FRAMES and server is up.
		if (this.numOfFrames <= MAX_WMTS_FRAMES) {
			this.gitcServerIsUp ??= await this.checkWTMSServer();

			if (this.gitcServerIsUp) {
				const queryEndDate = animationEndDate && cloneDate(animationEndDate);

				const todayISO = (new Date()).toISOString();

				const endDate = queryEndDate && !isNaN(queryEndDate.valueOf()) ?
					queryEndDate
					: getDateFromUTCDateStr(todayISO);

				// Is endDate today? Update isLatest
				isLatest = localDateToNoonUTC(endDate).valueOf() === localDateToNoonUTC(getDateFromUTCDateStr(todayISO)).valueOf();

				let foundFirstWMTS = false;

				for (const i of [...Array(MAX_DEEP_CHECKS).keys()]) {
					// End the loop if we reach our num of days requirement.
					if (framesData.length === this.numOfFrames) {
						break;
					}

					const currDate = cloneDate(endDate);
					i > 0 && subtractDaysFromDate(currDate, i);
					const currDateStr = dateToUTCString(currDate);

					for (const { type, title, endpoint, layer, template, minLevel = 2 } of FALLBACKS) {
						if (type === 'wmts') {
							const url = template.replace('{YYYY-MM-DD}', currDateStr);
							const top = HEIGHT - Math.max(0, 10 - 19 * Math.cos((currDate.getMonth() + 1) / 6 * Math.PI));
							const bottom = Math.max(0, 10 + 15 * Math.cos((currDate.getMonth() + 1) / 6 * Math.PI));

							const blackness = await this.calculateBlackness(url, Math.round(bottom), Math.round(top), MAX_BLACKNESS);

							// If current WMTS is valid and does not have too much black, we use it and stop checking further
							if (blackness <= MAX_BLACKNESS) {
								const info = {
									type,
									title,
									endpoint,
									layer,
									minLevel,
									date: currDateStr,
									dateObject: currDate
								};

								if (!foundFirstWMTS) {
									foundFirstWMTS = true;
									const wmts = this.setupWMTS('viirs', info);
									wmts.setVisible(true);
								}

								framesData.push(info);
								break;
							}
						} else {
							// Check existing dataset animation data (viirs cubemap)
							const { tilePathPrefix } = animations.find(({ date }) => date === currDate.toISOString()) || {};

							if (tilePathPrefix) {
								framesData.push({
									type: cubeFallbackType,
									title,
									date: currDateStr,
									dateObject: currDate,
									url: `${Config.dynamicAssetsUrl}/earth/data/${tilePathPrefix}$FACE.png`
								});
								// No break necessary if we're already at the last fallback
							}
						}
					}
				}
			}
		}

		if (framesData.length < this.numOfFrames) {
			const [,, cubeMapFallback] = FALLBACKS;
			const { type, title } = cubeMapFallback;

			// If there is no framesData, it means we're not using WMTS at all (likely the wider date range).
			if (framesData.length === 0) {
				animations.forEach(({ date, tilePathPrefix }) => {
					const currDate = getDateFromUTCDateStr(date);
					framesData.push({
						type,
						date: dateToUTCString(currDate),
						title,
						url: `${Config.dynamicAssetsUrl}/earth/data/${tilePathPrefix}$FACE.png`,
						dateObject: currDate
					});
				});
				// In this case, we don't need to update the animation props
				updateAnimationProps = false;
			} else {
				// We have some frames missing, despite checking <MAX_DEEP_CHECKS> times.
				// Let's get the remaining dates using viirs cubemap data.
				const { frequency, startDate: earliestDate, endDate: latestDate, missingDates: manifestMissingDates } = getDatasetManifestData() || {};

				// Here we can include the existing framesData into the missingDates arg for getLatestDates.
				const missingDates = [...manifestMissingDates, ...framesData.map(({ date }) => date)];
				const numMissingFrames = this.numOfFrames - framesData.length;

				const requiredDates = getLatestDates({
					frequency,
					startDate: getDateFromUTCDateStr(earliestDate),
					endDate: getDateFromUTCDateStr(latestDate),
					missingDates
				}, numMissingFrames);


				requiredDates.forEach(dateObject => {
					framesData.push({
						type,
						date: dateToUTCString(dateObject),
						title,
						url: `${Config.dynamicAssetsUrl}/earth/data/viirsToday/${calcPrefixFromDate(dateObject)}$FACE.png`,
						dateObject
					});
				});
			}
		}


		// Sort data by ascending date, ie. starting with earliest.
		this.currentData = framesData.sort((a, b) => {
			if (b.dateObject.valueOf() < a.dateObject.valueOf()) {
				return 1;
			}
			if (b.dateObject.valueOf() > a.dateObject.valueOf()) {
				return -1;
			}
			return 0;
		});


		// Update current index and animation props.
		if (this.currentData?.length) {
			this.currentIndex = this.currentData.length - 1;

			this.preload();

			// Update dataset animation props and latest bools if needed.
			// Also determine how many missing dates we have.
			if (updateAnimationProps) {
				setIsLatest(!hasAnimation && isLatest);
				setIsLatestSeven(hasAnimation && isLatest && this.currentData.length === 7);

				setAnimationProps({ animations: this.currentData });
			}
		} else {
			// fallback on blue marble
			const { get } = refsStore.stateSnapshot;
			const sceneManager = get('scene');

			sceneManager.toggleTexture('bluemarble');

			const spheroid = this.earth.getComponentByType('spheroidLOD');
			spheroid?.setTexture('decal', '$STATIC_ASSETS_URL/maps/earth/cloud_$SIZE_$FACE.png', [16, 512, 2048, 4096]);
			spheroid?.setFeature('decalMap', true);
		}
	}

	async enableWMTS() {
		const promises = [];
		for (let i = 0; i < this.currentData?.length; i++) {
			const { type } = this.currentData[i];
			const wmts = this.earth.getComponent(`viirs${i}`);

			if (type === 'wmts' && wmts) {
				wmts.setEnabled(true);
				wmts.setVisible(false);

				await this.app.pioneer.waitUntilNextFrame();
				promises.push(wmts.getTilesLoadedPromise());
			}
		}

		return promises;
	}

	changeIndex(index) {
		const { get } = refsStore.stateSnapshot;
		const { vitalSignId } = get('content');
		if (vitalSignId !== 'visible_earth') {
			return;
		}

		if (!this.currentData) {
			return;
		}

		// Handle WMTS case for Visible Earth by loading the wmts object for the correct date.
		this.hide(true);

		this.currentIndex = index % this.numOfFrames;
		const { type, url: cubeTexturePath } = this.currentData[this.currentIndex];

		if (type === 'wmts') {
			const wmtsCurrent = this.earth?.getComponent(`viirs${this.currentIndex}`);
			if (wmtsCurrent) {
				wmtsCurrent.setEnabled(true);
				wmtsCurrent.setVisible(true);
			}
		} else if (type === 'cube') {
			// Trigger cube map setup.
			const lodCurrent = this.setupCubemap(`cube${this.currentIndex}`, cubeTexturePath);

			if (lodCurrent) {
				lodCurrent.setEnabled(true);
				lodCurrent.setVisible(true);
			}
		}

		for (let i = 0; i < this.numOfFrames; i += 1) {
			const wmts = this.earth?.getComponent(`viirs${i}`);
			if (wmts && i !== this.currentIndex) {
				wmts.setVisible(false);
			}

			const lod = this.earth?.getComponent(`cube${i}`);
			if (lod && i !== this.currentIndex) {
				lod.setVisible(false);
			}
		}

		this.setTitle();
	}

	/**
	 * Updates WMTS max level on a dataset store update
	 */
	updateWMTSMaxLevel({ isPaused, hasAnimation }) {
		const wmtsCurrent = this.earth?.getComponent(`viirs${this.currentIndex}`);

		if (!wmtsCurrent) {
			return;
		}

		const hiRes = !hasAnimation || isPaused;
		const maxLevel = hiRes ? Infinity : wmtsCurrent.getMinLevel();

		wmtsCurrent.setMaxLevel(maxLevel);
	}

	setTitle() {
		if (!this.currentData || !this.currentData[this.currentIndex]?.title) {
			return;
		}

		const { currentDataset } = datasetStore.stateSnapshot;
		const updatedDataset = JSON.parse(JSON.stringify(currentDataset));

		if (!updatedDataset) {
			return;
		}

		updatedDataset.title = this.currentData[this.currentIndex]?.title;
		datasetStore.setGlobalState({ currentDataset: updatedDataset });
	}

	async init() {
		// First hide previous.
		this.earth.getComponentByType('atmosphere')?.setEnabled(false);

		this.hide();

		// Set current data.
		await this.setCurrentData();

		// Disable earth's basic spheroidLOD.
		this.earth.getComponentByType('spheroidLOD')?.setEnabled(false);

		if (!this.currentData?.length) {
			return;
		}

		const mostRecentData = this.currentData[this.currentData.length - 1];
		const { type, url: cubeTexturePath } = mostRecentData || {};

		let component = null;
		// Set to latest wmts/cubemap.
		if (type === 'wmts') {
			const cubeComponent = this.earth.getComponent(`cube${this.currentIndex}`);
			if (cubeComponent?.isEnabled()) {
				cubeComponent.setEnabled(false);
			}
			component = this.earth.getComponent('viirs') || this.setupWMTS('viirs', mostRecentData);
		} else if (cubeTexturePath) {
			const virrsComponent = this.earth.getComponent('viirs');
			if (virrsComponent?.isEnabled()) {
				virrsComponent.setEnabled(false);
			}
			component = await this.setupCubemap(`cube${this.currentIndex}`, cubeTexturePath);
		}

		if (component === null) {
			return;
		}

		component.setEnabled(true);
		component.setVisible(true);

		// always turn off blue marble toggle when WMTS is shown
		layersStore.setGlobalState({ blueMarble: false });

		// Update title.
		this.setTitle();
	}

	hide(all = false) {
		if (all) {
			this.earth.getComponent('viirs')?.setVisible(false);
		}

		if (this.currentData?.length) {
			for (const i in this.currentData) {
				this.earth.getComponent(`viirs${i}`)?.setVisible(false);
				this.earth.getComponent(`cube${i}`)?.setVisible(false);
			}
		}
	}


	destroy() {
		if (this.earth.getComponent('viirs') !== null) {
			this.earth.removeComponent('viirs');
		}

		for (let i = 0; i < this.numOfFrames; i += 1) {
			if (this.earth.getComponent(`viirs${i}`) !== null) {
				this.earth.removeComponent(`viirs${i}`);
			}

			if (this.earth.getComponent(`cube${i}`) !== null) {
				this.earth.removeComponent(`cube${i}`);
			}
		}
	}
}

export default VisibleEarthManager;
